import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import SEO from "../components/SEO";
import "../style.css";

const whatsappNumber = "918838619488";

const normalizeImageValues = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : [trimmed];
      } catch {
        return [trimmed];
      }
    }

    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [trimmed];
  }

  if (typeof value === "object") {
    return Object.values(value);
  }

  return [];
};

const getProductImages = (laptop) => {
  if (!laptop) return [];

  const listFromImages = normalizeImageValues(laptop.images);
  const listFromGallery = normalizeImageValues(laptop.gallery);
  const legacyImages = [
    laptop.image,
    laptop.image1,
    laptop.image2,
    laptop.image3,
  ];

  const merged = [...listFromImages, ...listFromGallery, ...legacyImages]
    .map((image) => String(image || "").trim())
    .filter(Boolean);

  const uniqueImages = [...new Set(merged)].slice(0, 3);
  return uniqueImages.length ? uniqueImages : ["placeholder.png"];
};

const getNumericPrice = (value) => {
  const price = Number(String(value || "").replace(/[^\d.]/g, ""));
  return Number.isFinite(price) ? price : 0;
};

function ProductDetails() {
  const { id } = useParams();
  const [laptop, setLaptop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const loadLaptop = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const snap = await getDoc(doc(db, "laptops", id));
      if (!snap.exists()) {
        setLaptop(null);
      } else {
        const data = { _id: snap.id, ...snap.data() };
        setLaptop(data);
      }
    } catch (err) {
      console.error("Error loading laptop:", err);
      setLaptop(null);
      setLoadError("Failed to load product details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadLaptop();
  }, [loadLaptop]);

  const productImages = useMemo(() => getProductImages(laptop), [laptop]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [id]);

  useEffect(() => {
    if (activeImageIndex >= productImages.length) {
      setActiveImageIndex(0);
    }
  }, [activeImageIndex, productImages.length]);

  const showPreviousImage = () => {
    if (productImages.length <= 1) return;
    setActiveImageIndex(
      (previous) => (previous - 1 + productImages.length) % productImages.length
    );
  };

  const showNextImage = () => {
    if (productImages.length <= 1) return;
    setActiveImageIndex((previous) => (previous + 1) % productImages.length);
  };

  const displayPrice = laptop?.discountPrice || laptop?.price;
  const canonicalUrl = `https://lappylaptops.com/products/${id}`;
  const seoImage = productImages[0] || "/lappy_logo_png.png";
  const compactSpecs = String(laptop?.specs || "").replace(/\s+/g, " ").trim();
  const seoDescription = laptop
    ? `${laptop.name} available at Lappy Used Laptops, Namakkal. ${compactSpecs}`.slice(
        0,
        160
      )
    : "View laptop details, specs and price at Lappy Used Laptops in Namakkal.";

  const priceValue = getNumericPrice(displayPrice);
  const productSchema = laptop
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: laptop.name,
        description: compactSpecs || "Used laptop available for sale in Namakkal.",
        image: productImages,
        brand: laptop.brand ? { "@type": "Brand", name: laptop.brand } : undefined,
        offers: priceValue
          ? {
              "@type": "Offer",
              url: canonicalUrl,
              priceCurrency: "INR",
              price: priceValue,
              availability: "https://schema.org/InStock",
            }
          : undefined,
      }
    : null;

  const discountPercent = laptop
    ? (() => {
        const price = Number(laptop.price);
        const discount = Number(laptop.discountPrice);
        if (!discount || !price || discount >= price) return null;
        return Math.round((1 - discount / price) * 100);
      })()
    : null;

  const getWhatsAppLink = () => {
    if (!laptop) return "#";
    const message = `Hello! I'm interested in this laptop:

Name: ${laptop.name}
Price: Rs ${displayPrice}
Specs: ${laptop.specs}`;

    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  };

  if (loading) {
    return (
      <div className="product-detail">
        <SEO
          title="Product Details"
          description="Loading product information from Lappy Used Laptops."
          url={canonicalUrl}
        />
        <div className="product-detail-card">Loading...</div>
      </div>
    );
  }

  if (!laptop) {
    if (loadError) {
      return (
        <div className="product-detail">
          <SEO
            title="Product Load Error"
            description="Unable to load this product right now."
            url={canonicalUrl}
            noindex
          />
          <div className="product-detail-card">
            <h2>Unable to load product</h2>
            <p>{loadError}</p>
            <div className="product-detail-actions">
              <button className="btn-primary" type="button" onClick={loadLaptop}>
                Retry
              </button>
            </div>
            <Link className="product-detail-back product-detail-back-btn" to="/products">
              <span className="product-detail-back-icon" aria-hidden="true">
                {"<-"}
              </span>
              <span>Back to Products</span>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="product-detail">
        <SEO
          title="Product Not Found"
          description="The requested laptop could not be found."
          url={canonicalUrl}
          noindex
        />
        <div className="product-detail-card">
          <h2>Product not found</h2>
          <Link className="product-detail-back product-detail-back-btn" to="/products">
            <span className="product-detail-back-icon" aria-hidden="true">
              {"<-"}
            </span>
            <span>Back to Products</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail">
      <SEO
        title={`${laptop.name} - Used Laptop`}
        description={seoDescription}
        url={canonicalUrl}
        image={seoImage}
        type="product"
        structuredData={productSchema}
      />
      <div className="product-detail-card">
        <div className="product-detail-head">
          <Link className="product-detail-back product-detail-back-btn" to="/products">
            <span className="product-detail-back-icon" aria-hidden="true">
              {"<-"}
            </span>
            <span>Back to Products</span>
          </Link>

          {discountPercent && (
            <span className="product-detail-off-pill">{discountPercent}% OFF</span>
          )}
        </div>

        <div className="product-detail-grid">
          <div className="product-detail-media">
            <div className="product-detail-main">
              {discountPercent && (
                <span className="discount-badge-top">{discountPercent}% OFF</span>
              )}
              <img
                src={productImages[activeImageIndex] || "placeholder.png"}
                alt={laptop.name}
                onError={(e) => (e.currentTarget.src = "placeholder.png")}
              />
            </div>

            {productImages.length > 1 && (
              <>
                <div className="product-detail-gallery-controls">
                  <button type="button" onClick={showPreviousImage}>
                    Prev
                  </button>
                  <button type="button" onClick={showNextImage}>
                    Next
                  </button>
                </div>

                <div className="product-detail-thumbs">
                  {productImages.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      className={`product-detail-thumb ${
                        index === activeImageIndex ? "active" : ""
                      }`}
                      onClick={() => setActiveImageIndex(index)}
                      aria-label={`Show image ${index + 1}`}
                    >
                      <img
                        src={image}
                        alt={`${laptop.name} ${index + 1}`}
                        onError={(e) => (e.currentTarget.src = "placeholder.png")}
                      />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="product-detail-info">
            <p className="product-detail-label">READY TO USE THE LAPTOPS</p>
            <h1>{laptop.name}</h1>

            <div className="price-row price-row-left">
              {laptop.discountPrice ? (
                <>
                  <span className="price-new">Rs {laptop.discountPrice}</span>
                  <span className="price-old">Rs {laptop.price}</span>
                </>
              ) : (
                <span className="price-new">Rs {laptop.price}</span>
              )}
            </div>

            <h3>Specifications</h3>
            <p className="product-detail-specs">{laptop.specs}</p>

            <div className="product-detail-actions">
              <a
                className="btn-secondary whatsapp-btn"
                href={getWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetails;
